/**
 * Pure UI policy for the Liquidación section's month-based bulk-settlement
 * selector (owner UX batch, 2026-09-02). No I/O, no DOM — same split as
 * `js/shared/timesheet-policy.js`, but this module is intentionally NOT a
 * parity twin of anything in `api/_lib/`: the API contract
 * (`api/liquidacion.js`) still only understands `desde`/`hasta` day-range
 * strings. S8: the legacy month-only bulk-settlement selector this module
 * originally served was retired (`js/admin.js`'s legacy
 * `handleLiquidacionPreviewSubmit`, removed — see apply-progress.md
 * "Unit S8"); `mesARango` lives on, reused by the tablero's "Exportar CSV"
 * (moved from the legacy toolbar). The month picker is a client-only
 * presentation convenience that gets
 * translated to that same day-range contract BEFORE the request leaves the
 * browser. Because the server never sees a "month", there is nothing to keep
 * in parity server-side, and this file deliberately lives outside the
 * `timesheet-policy.js` parity pair (its own header documents why adding an
 * unrelated export there would be the wrong place to look for this rule).
 */

/**
 * Converts a calendar month (`YYYY-MM`) to the inclusive day-range the
 * settlement API already understands: the first and last calendar day of
 * that month. Leap years are handled by delegating the "what is the last day
 * of this month" question to `Date.UTC` (day `0` of the FOLLOWING month, in
 * UTC, is always the last real day of the TARGET month — never a fixed
 * 28/30/31 lookup table that would need its own leap-year special case).
 *
 * `null` for anything that is not a well-formed `YYYY-MM` string with a
 * month between 01 and 12 — never a guess, never a silently-clamped range.
 * S8: the caller is now `js/admin.js#handleTableroExportarClick`, which
 * treats `null` as "nothing to export" the same way the retired legacy
 * submit path treated it as "nothing to submit".
 * @param {string} mes `YYYY-MM`
 * @returns {{ desde: string, hasta: string } | null}
 */
export function mesARango(mes) {
    if (typeof mes !== 'string') return null;
    const match = /^(\d{4})-(\d{2})$/.exec(mes);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]); // 1-indexed calendar month (01-12)
    if (month < 1 || month > 12) return null;

    const desde = `${match[1]}-${match[2]}-01`;
    // `Date.UTC(year, month, 0)` — `month` here is already the 1-indexed
    // target month, which JS's 0-indexed Date constructor reads as "day 0 of
    // the NEXT month" — i.e. the last real day of the TARGET month. This is
    // correct for every month including December (rolls into January of
    // `year + 1` internally, still yields December 31 of `year`) without any
    // month-specific branching.
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const hasta = `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`;
    return { desde, hasta };
}

/**
 * Default month pre-filled in the "Mes a liquidar" picker on first entry to
 * the tab (Req: month selector default). Owner's decision (2026-09-02,
 * simplified from an earlier day-of-month-dependent draft): ALWAYS the
 * previous calendar month, regardless of today's day-of-month — a
 * day-sensitive default ("previous month before the 10th, else current
 * month") was considered and explicitly rejected as unnecessary complexity
 * for a value the admin can always change before previewing.
 * @param {Date} [now]
 * @returns {string} `YYYY-MM`
 */
export function mesLiquidacionPorDefecto(now = new Date()) {
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = prevMonthDate.getFullYear();
    const month = String(prevMonthDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
}
